import * as client from "_graphql-types";
import { filterSortPage } from "../DataProvider";
import * as types from "./types";
import uuid from "frontend/src/utils/uuid";

type Sdk = types.Sdk;

interface DocumentQueueParams {
  id: string;
  name: string;
  userId: number;
  queueDate: string;
  documentsId?: number;
  hash: string;
  emailMaps?: Array<{
    id: string;
    email: string;
  }>;
}

export type DocumentQueueCreateParams = types.CreateParams<DocumentQueueParams>;

// Data Provider Functions

async function getList(
  sdk: Sdk,
  params: types.GetListParams<client.DocumentQueueSortEnum>
) {
  const { pagination, filter, sort } = params;

  const req = filterSortPage({
    filter,
    sort,
    pagination,
  });
  req.sort = [
    ...req.sort,
    {
      field: client.DocumentQueueSortEnum.EmailId,
      order: client.SortInput.Desc,
    },
  ];

  const res = await sdk.GetDocumentQueueList(req);

  return {
    data: res.documentQueueList?.items,
    total: res.documentQueueList?.total,
  };
}

async function getManyReference(
  sdk: Sdk,
  params: types.GetManyReferenceParams<client.DocumentQueueSortEnum>
) {
  const { pagination, sort, filter, target } = params;

  const res = await sdk.GetDocumentQueueList({
    filter: { ...filter, [target]: params.id },
    sort: [{ field: sort.field, order: sort.order }],
    page: {
      offset: (pagination.page - 1) * pagination.perPage,
      limit: pagination.perPage,
    },
  });

  return {
    data: res.documentQueueList?.items,
    total: res.documentQueueList?.total,
  };
}

async function getOne(sdk: Sdk, params: types.GetOneParams<string>) {
  const { documentQueue: data } = await sdk.GetOneDocumentQueue({
    id: params.id,
  });

  return { data };
}

async function update(
  sdk: Sdk,
  params: types.UpdateParams<
    NonNullable<client.DocumentQueuePublishInput> & {
      overrideEntities?: Record<string, Array<{ id: number; name?: string }>>;
    }
  >
) {
  if (!params.data.documentTypeEnumId) {
    throw new Error("documentTypeEnumId is required");
  }

  if (!params.data.date) {
    throw new Error("date is required");
  }

  // Build entities array from overrideEntities (keyed by businessObjectEnumId)
  const entities: Array<{ businessObjectEnumId: number; entityId: number }> =
    [];
  const overrideEntities = params.data.overrideEntities;
  if (overrideEntities) {
    for (const [typeId, entityList] of Object.entries(overrideEntities)) {
      const businessObjectEnumId = Number(typeId);
      if (Array.isArray(entityList)) {
        for (const entity of entityList) {
          if (entity?.id) {
            entities.push({ businessObjectEnumId, entityId: entity.id });
          }
        }
      }
    }
  }

  const input: Partial<client.DocumentQueuePublishInput> = {
    documentQueueId: String(params.id),
    documentTypeEnumId: params.data.documentTypeEnumId,
    date: new Date(params.data.date).toISOString().split("T")[0],
    accessLevel: params.data.accessLevel,
    entities: entities.length > 0 ? entities : undefined,
    isGallery: params.data.isGallery,
    documentSubTypeEnumId: params.data.documentSubTypeEnumId,
    allowDuplicateFile: params.data.allowDuplicateFile,
    documentGroupId: null,
    knowledgeBaseTagIds: params.data.knowledgeBaseTagIds,
    name: params.data.name,
  };

  await sdk.TransferToDocuments({
    input: [input as client.DocumentQueuePublishInput],
  });

  return await getOne(sdk, { id: String(params.id) });
}

async function deleteOne(sdk: Sdk, params: types.DeleteParams<string>) {
  await sdk.DeleteDocumentQueue({
    id: params.id,
  });
  return { data: { id: params.id } };
}

const create = async (
  sdk: Sdk,
  param: types.CreateParams<{ files: File[] }>
) => {
  if (!param.data.files) {
    throw new Error("No file to upload");
  }

  const files = param.data.files.map(file => ({ file }));

  const names = files.map(file => file.file.name);

  const data = await sdk.addDocumentQueueSignedURI({
    names,
  });

  return {
    data: {
      id: "bulk" + uuid(),
      drafts: data.addDocumentQueueSignedURI.map((d, i) => ({
        ...d,
        file: files[i].file,
      })),
    },
  };
};

export const documentQueue = types.dataProvider({
  getList,
  getOne,
  getManyReference,
  update,
  delete: deleteOne,
  create,
});
